import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';

import { RootState } from 'editor/src/store';

import { MediaElement, Spread } from '../types';

import getSpreadCountForIncrement from './getSpreadCountForIncrement';
import planSpreadRemoval from './planSpreadRemoval';

export interface SpreadRemovalContent {
  // Pages one "delete pages" step at this spread takes away.
  pages: number;
  // Whether any of them holds photos or text of the user's own. Template samples do not count:
  // replacing them is what the user is there to do.
  holdsContent: boolean;
}

// A buyer can neither change nor remove what the template fixes (hidden, personalisation-locked or
// static artwork) and an empty photo frame holds nothing yet, so neither is theirs to lose. What
// they add is theirs even when the host locks new elements by default. For the template's author
// every element is their own work.
function isUsersContent(element: MediaElement, isBuyer: boolean): boolean {
  if ('sample' in element && element.sample) {
    return false;
  }
  if (!isBuyer) {
    return true;
  }
  if (element.type === 'image' && !element.imageId) {
    return false;
  }
  if (element.createdWhileEmbedded) {
    return true;
  }
  return !element.hidden && !element.personalizationLocked && !element.static;
}

// A page background is not counted: template spreads carry their own, so a freshly added spread
// would ask too.
export function spreadsHoldContent(spreads: Spread[], isBuyer: boolean): boolean {
  return spreads.some((spread) =>
    spread.pages.some((page) => page.groups.media?.some((element) => isUsersContent(element, isBuyer))),
  );
}

// What a delete at `spreadIndex` would cost the user, so desktop and mobile ask the same question
// under the same rule.
function spreadRemovalHoldsContent(state: RootState, spreadIndex: number): SpreadRemovalContent {
  const { designData } = state.design;
  if (!designData) {
    return { pages: 0, holdsContent: false };
  }

  const spreadCount = getSpreadCountForIncrement(designData, spreadIndex, 'remove', getECommercePageCounts(state));
  const { removedSpreads } = planSpreadRemoval(designData, spreadIndex, spreadCount);
  const isBuyer = !state.hostSettings.ignorePersonalizationLock;
  return {
    pages: removedSpreads.reduce((pages, spread) => pages + spread.pages.length, 0),
    holdsContent: spreadsHoldContent(removedSpreads, isBuyer),
  };
}

export default spreadRemovalHoldsContent;
